import config from '../../config';

const BASE_URL = config.apiUrl;

export type ShareAccess = 'public' | 'password' | 'email';
export type ShareLayout = 'brief' | 'transcript';

export interface OwnerShare {
  active: boolean;
  token?: string;
  access?: ShareAccess;
  layout?: ShareLayout;
  has_brief?: boolean;
  show_transcript?: boolean;
  emails?: string[];
  has_password?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ShareMeta {
  access: ShareAccess;
  layout?: ShareLayout;
  unlocked: boolean;
  title: string | null;
}

export interface SharedMessage {
  id: number;
  role: 'user' | 'assistant' | string;
  content: string;
  sources?: any;
  created_at?: string | null;
}

export interface ShareMessages {
  title: string;
  layout?: ShareLayout;
  brief?: string | null;
  show_transcript?: boolean;
  messages: SharedMessage[];
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType') || 'Bearer';
  return token ? { Authorization: `${tokenType} ${token}` } : {};
};

export const sharePageUrl = (token: string): string =>
  `${window.location.origin}/s/${token}`;

export const shareSessionKey = (token: string): string =>
  `powerai.shareSession.${token}`;

export const readShareSession = (token: string): string | null =>
  localStorage.getItem(shareSessionKey(token));

export const writeShareSession = (token: string, jwt: string): void => {
  localStorage.setItem(shareSessionKey(token), jwt);
};

const detailMessage = async (res: Response, fallback: string): Promise<string> => {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message;
    }
    if (typeof detail === 'string') return detail;
  } catch {
    /* ignore */
  }
  return fallback;
};

const ownerUrl = (uuid: string) => `${BASE_URL}/conversations/uuid/${uuid}/link-share`;

export async function getOwnerShare(uuid: string): Promise<OwnerShare> {
  const res = await fetch(ownerUrl(uuid), { headers: authHeaders() });
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal memuat tautan.'));
  return res.json();
}

export async function putOwnerShare(
  uuid: string,
  body: {
    access: ShareAccess;
    password?: string;
    emails?: string[];
    layout?: ShareLayout;
    refresh_brief?: boolean;
    show_transcript?: boolean;
  },
): Promise<OwnerShare> {
  const res = await fetch(ownerUrl(uuid), {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal menyimpan tautan.'));
  return res.json();
}

export async function deleteOwnerShare(uuid: string): Promise<OwnerShare> {
  const res = await fetch(ownerUrl(uuid), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal menutup tautan.'));
  return res.json();
}

export async function rotateOwnerShare(uuid: string): Promise<OwnerShare> {
  const res = await fetch(`${ownerUrl(uuid)}/rotate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal membuat tautan baru.'));
  return res.json();
}

const shareHeaders = (token: string): Record<string, string> => {
  const jwt = readShareSession(token);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

export async function getShareMeta(token: string): Promise<ShareMeta> {
  const res = await fetch(`${BASE_URL}/shares/${token}`, { headers: shareHeaders(token) });
  if (res.status === 404) throw new Error('Tautan ini sudah tidak berlaku.');
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal membuka tautan.'));
  return res.json();
}

export async function getShareMessages(token: string): Promise<ShareMessages> {
  const res = await fetch(`${BASE_URL}/shares/${token}/messages`, {
    headers: shareHeaders(token),
  });
  if (res.status === 401) throw new Error('Buka tautan ini dulu.');
  if (res.status === 404) throw new Error('Tautan ini sudah tidak berlaku.');
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal memuat percakapan.'));
  return res.json();
}

export async function unlockShare(token: string, password: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/shares/${token}/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Kata sandi salah.'));
  const data = await res.json();
  if (data.access_token) writeShareSession(token, data.access_token);
}

export async function requestShareOtp(token: string, email: string): Promise<string> {
  const res = await fetch(`${BASE_URL}/shares/${token}/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Gagal mengirim kode.'));
  const data = await res.json();
  return data.message || 'Kalau email ini diizinkan, kode sudah dikirim.';
}

export async function verifyShareOtp(token: string, email: string, code: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/shares/${token}/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) throw new Error(await detailMessage(res, 'Kode salah atau kedaluwarsa.'));
  const data = await res.json();
  if (data.access_token) writeShareSession(token, data.access_token);
}
